import { objectFilter, objectMap, objectValuesMap } from "./mapping";
import {
	SELECT_FIELD_TYPE,
	TEXT_FIELD_TYPE,
	CHECKBOX_FIELD_TYPE,
	DATE_FIELD_TYPE,
	VALID_FIELD_TYPES,
	CONDITION_ISCHECKED,
	CONDITION_ISEMPTY,
	CONDITION_RANGE,
	CONDITION_EQUAL,
	CONDITION_ISDIFFERENT,
	CONDITION_STARTSWITH,
	CONDITION_GT,
	CONDITION_GTE,
	CONDITION_LT,
	CONDITION_LTE,
	BUYER_ENTRY_TYPE
} from "../models/forms-with-entry-usage";

export const extractFormFieldFilter = formFieldFilter => {
	return Array.isArray(formFieldFilter)
		? formFieldFilter
		: formFieldFilter?.form_field_filter || formFieldFilter;
};

export const paramsToFilters = (filterKeys = [], params) => {
	return objectValuesMap(
		objectFilter(
			params,
			([k, v]) =>
				filterKeys.includes(k) && (v !== null && v !== undefined)
		),
		(v, k) => {
			if (filterKeys.includes(k)) {
				if (k === "status" || k === "searchType") {
					return v;
				}
				if (k === "page" || k === "pageSize") {
					return typeof v === "number" ? v : Number(v);
				}
				if (k === "form_field_filter") {
					if (typeof v === "string") {
						try {
							const parsed = JSON.parse(v);
							if (Array.isArray(parsed)) {
								return parsed;
							}
							if (
								typeof parsed === "object" &&
								parsed !== null &&
								parsed.fieldId &&
								parsed.condition
							) {
								return [parsed];
							}
						} catch (e) {
							console.error(
								"Failed to parse form_field_filter:",
								e
							);
							return [];
						}
					}
					if (Array.isArray(v)) {
						return v;
					}
					if (
						typeof v === "object" &&
						v !== null &&
						!Array.isArray(v) &&
						v.fieldId &&
						v.condition
					) {
						return [v];
					}
					return [];
				}
				return new Set(typeof v !== "object" ? [v] : v);
			}
		}
	);
};

export const filtersToParams = (filterKeys = [], filters) => {
	return objectMap(
		objectFilter(
			{ ...Object.fromEntries(filterKeys.map(k => [k, {}])), ...filters },
			([k, v]) =>
				filterKeys.includes(k) &&
				(k === "status" ||
				k === "searchType" ||
				k === "form_field_filter"
					? true
					: v !== null && v !== undefined)
		),
		([k, v]) => {
			if (filterKeys.includes(k)) {
				if (k === "status") {
					if (v === undefined) {
						return [k, "*"];
					}
					if (v instanceof Set) {
						const arr = Array.from(v);
						return [
							k,
							arr.length > 0 ? arr.map(String) : undefined
						];
					}
					if (Array.isArray(v)) {
						return [k, v.length > 0 ? v.map(String) : undefined];
					}
					if (typeof v === "string" && v.length > 0) {
						return [k, v];
					}
					return [k, undefined];
				}

				if (k === "searchType") {
					if (v instanceof Set) {
						const arr = Array.from(v);
						return [k, arr.length > 0 ? arr[0] : undefined];
					}
					if (Array.isArray(v)) {
						return [k, v.length > 0 ? v[0] : undefined];
					}
					if (typeof v === "string" && v.length > 0) {
						return [k, v];
					}
					return [k, undefined];
				}

				if (k === "form_field_filter") {
					const actualValue =
						v?.form_field_filter !== undefined && !Array.isArray(v)
							? v.form_field_filter
							: v;

					const isValidFilter = filter =>
						filter &&
						typeof filter === "object" &&
						filter.fieldId &&
						filter.condition &&
						String(filter.condition).trim();

					if (Array.isArray(actualValue) && actualValue.length > 0) {
						const validFilters = actualValue.filter(isValidFilter);
						return validFilters.length > 0
							? [k, JSON.stringify(validFilters)]
							: [k, undefined];
					}

					if (
						actualValue &&
						typeof actualValue === "object" &&
						!Array.isArray(actualValue) &&
						isValidFilter(actualValue)
					) {
						return [k, JSON.stringify(actualValue)];
					}

					return [k, undefined];
				}

				if (typeof v === "number") {
					return [k, String(v)];
				}

				if (typeof v === "string" && v.length > 0) {
					return [k, v];
				}

				if (!v.size && !v.length) {
					return [k, undefined];
				}

				return [k, [...v].map(String)];
			}
		}
	);
};

export const cleanFiltersForQuery = filters => {
	return Object.fromEntries(
		Object.entries(filters ?? {})
			.map(([key, value]) => {
				if (value instanceof Set) {
					return [key, Array.from(value).join(",")];
				}
				return [key, value];
			})
			.filter(([_, v]) => v !== undefined && v !== null && v !== "")
	);
};

const convertToValidType = value => {
	if (typeof value === "string" && !isNaN(value)) {
		const num = Number(value);
		return isNaN(num) ? value : num;
	}
	return value;
};

export const convertFiltersToValidTypes = filters => {
	if (!filters || typeof filters !== "object") {
		return filters;
	}

	return Object.fromEntries(
		Object.entries(filters).map(([key, value]) => {
			if (value === null || value === undefined) {
				return [key, value];
			}
			if (key === "form_field_filter") {
				if (Array.isArray(value)) {
					return [key, value];
				}
				if (typeof value === "object" && !Array.isArray(value)) {
					return [key, value];
				}
			}
			if (value instanceof Set) {
				return [key, Array.from(value).map(convertToValidType)];
			}

			if (Array.isArray(value)) {
				return [key, value.map(convertToValidType)];
			}

			if (typeof value === "string") {
				return [key, convertToValidType(value)];
			}
			return [key, value];
		})
	);
};

const formatDate = date => {
	if (!date) return null;
	if (typeof date === "string") return date.split("T")[0];
	if (date.toISOString) return date.toISOString().split("T")[0];
	if (date.format) return date.format("YYYY-MM-DD");
	return null;
};

const inferFieldType = (fieldType, condition, value) => {
	if (fieldType === SELECT_FIELD_TYPE) return SELECT_FIELD_TYPE;
	if (fieldType && VALID_FIELD_TYPES.has(fieldType)) return fieldType;

	if (condition === CONDITION_ISCHECKED) return CHECKBOX_FIELD_TYPE;
	if (condition === CONDITION_RANGE) return DATE_FIELD_TYPE;
	if (condition === CONDITION_EQUAL && Array.isArray(value))
		return SELECT_FIELD_TYPE;

	return TEXT_FIELD_TYPE;
};

const conditionHandlers = {
	[CONDITION_ISCHECKED]: (fieldParam, value) => ({
		key: `${fieldParam}__ischecked`,
		value: value === true || value === "true" || value === 1 ? true : false
	}),

	[CONDITION_ISEMPTY]: (fieldParam, value) => ({
		key: `${fieldParam}__isempty`,
		value: !(value === false || value === "false") ? "True" : "False"
	}),

	[CONDITION_RANGE]: (fieldParam, value) => {
		if (!value) return null;
		const start = formatDate(value.value_start);
		const end = formatDate(value.value_end);
		if (start && end) {
			return { key: `${fieldParam}__range`, value: `${start},${end}` };
		}
		if (start) return { key: `${fieldParam}__gte`, value: start };
		if (end) return { key: `${fieldParam}__lte`, value: end };
		return null;
	},

	[CONDITION_ISDIFFERENT]: (fieldParam, value) => ({
		key: `${fieldParam}__isdifferent`,
		value: String(value || "")
	}),

	[CONDITION_STARTSWITH]: (fieldParam, value) => ({
		key: `${fieldParam}__istartswith`,
		value: String(value || "")
	}),

	[CONDITION_GT]: (fieldParam, value) => {
		const date = formatDate(value);
		return date ? { key: `${fieldParam}__gt`, value: date } : null;
	},

	[CONDITION_GTE]: (fieldParam, value) => {
		const dateValue = value?.value_start || value;
		const date = formatDate(dateValue);
		return date ? { key: `${fieldParam}__gt`, value: date } : null;
	},

	[CONDITION_LT]: (fieldParam, value) => {
		const date = formatDate(value);
		return date ? { key: `${fieldParam}__lt`, value: date } : null;
	},

	[CONDITION_LTE]: (fieldParam, value) => {
		const dateValue = value?.value_start || value;
		const date = formatDate(dateValue);
		return date ? { key: `${fieldParam}__lte`, value: date } : null;
	},

	[CONDITION_EQUAL]: (fieldParam, value, fieldType, inferredType) => {
		const isSelectType =
			fieldType === SELECT_FIELD_TYPE ||
			inferredType === SELECT_FIELD_TYPE;

		if (isSelectType && Array.isArray(value)) {
			return {
				key: fieldParam,
				value: value.map(String)
			};
		}

		return {
			key: fieldParam,
			value: String(value ?? "")
		};
	}
};

const convertFilterToParams = filter => {
	if (!filter?.fieldId || !filter?.condition) return null;

	const { fieldId, condition, value, entryType, fieldType } = filter;
	const prefix =
		entryType === BUYER_ENTRY_TYPE ? "buyer_form" : "attendee_form";
	const fieldParam = `${prefix}__${fieldId}`;
	const inferredType = inferFieldType(fieldType, condition, value);

	const handler = conditionHandlers[condition];
	if (!handler) return null;

	const result = handler(fieldParam, value, fieldType, inferredType);
	return result;
};

const mergeParams = (queryParams, key, value) => {
	if (Array.isArray(value)) {
		queryParams[key] = Array.isArray(queryParams[key])
			? [...queryParams[key], ...value]
			: value;
	} else {
		queryParams[key] = value;
	}
};

export const convertFormFieldFilterToQueryParams = formFieldFilter => {
	if (!formFieldFilter) return {};

	const filters = Array.isArray(formFieldFilter)
		? formFieldFilter
		: [formFieldFilter];
	const queryParams = {};

	filters.forEach(filter => {
		const result = convertFilterToParams(filter);
		if (result) {
			mergeParams(queryParams, result.key, result.value);
		}
	});

	return queryParams;
};

export const convertFiltersToApiParams = filters => {
	if (!filters || typeof filters !== "object") {
		return {};
	}

	const formFieldFilter = extractFormFieldFilter(filters.form_field_filter);
	const formFieldQueryParams = convertFormFieldFilterToQueryParams(
		formFieldFilter
	);

	const { form_field_filter: _, ...otherFilters } = filters;

	const validFilters = convertFiltersToValidTypes(otherFilters);

	return {
		...validFilters,
		...formFieldQueryParams
	};
};
